import { useGetAllDepartmentsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import {
  useGetAllOfferedApplicantsQuery,
  useUpdateHiringPipelineStatusMutation,
} from '@/app/features/recruitment/hiring-pipeline/hiring-pipeline.api';
import { useGetAllJobPostsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import ConfirmationModal from '@/components/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import ViewToggler from '@/components/ViewToggler';
import WithLoader from '@/components/WithLoader';
import { FILTER_KEYS } from '@/constants/keys.constants';
import { useAppFilters, useSelectionOptions } from '@/hooks';
import useDiscloser from '@/hooks/useDiscloser';
import {
  CheckIcon,
  ChevronDownIcon,
  PaperclipIcon,
  SearchIcon,
  UserMultipleIcon,
} from '@/icons';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { firstChar } from '@/utils/string.utils';
import { Button, Flex, Input, Select } from 'antd';
import { useEffect, useState } from 'react';
import EmpCard from '../Evaluation/_components/EvAssessment/EmpCard';
import OfferLetterDrawer from './_components/OfferLetterDrawer';

const SentOfferLetter = () => {
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drawerConfig, openDrawer, closeDrawer] = useDiscloser();
  const { page, limit, keyword, filters, handleFilter, handleSearch } =
    useAppFilters(FILTER_KEYS.HIRING_PIPELINE);
  const { select_status, ...others } = filters || {};
  const selectStatus = select_status;

  // ── Options ────────────────────────────────────────────────────────────────
  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    {
      labelKey: 'job_title',
      valueKey: 'id',
    },
  );

  const [departmentOptions, isDeptFetching] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: 'name',
      valueKey: 'id',
    },
  );

  // ── Data ───────────────────────────────────────────────────────────────────
  const {
    data: offeredData,
    isLoading,
    isFetching,
  } = useGetAllOfferedApplicantsQuery({
    params: {
      page,
      limit,
      search: keyword,
      ...others,
    },
  });

  const applicants = offeredData?.data?.data || [];

  const [updateHiringStatus, { isLoading: isHiring }] =
    useUpdateHiringPipelineStatusMutation();

  const handleHireApplicants = async () => {
    try {
      const payload = {
        offerLetterIds: selected,
        hiring_pipeline_status: 'hired',
      };
      const res = await updateHiringStatus(payload).unwrap();
      if (res?.success) {
        toast('success', getErrorMessage(res, 'Applicants hired successfully'));
        setSelected([]);
        handleFilter('select_status', false);
        setConfirmOpen(false);
      } else {
        toast('error', getErrorMessage(res, 'Failed to update hiring status'));
      }
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to update hiring status'));
    }
  };

  // ── Selection ──────────────────────────────────────────────────────────────
  const handleSelect = (id: string) => {
    if (!selectStatus) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    setSelected([]);
  }, [selectStatus]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Flex
        align="start"
        justify="space-between"
        gap={16}
        className="flex-wrap lg:flex-nowrap"
      >
        <ViewToggler disabled="list" mode="grid" setMode={() => {}} />
        <Flex vertical gap={12} align="end">
          <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
            <Input
              allowClear
              size="large"
              onChange={handleSearch}
              placeholder="Search Applicant"
              prefix={<SearchIcon size={20} />}
            />
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Job Position"
                popupMatchSelectWidth={false}
                loading={isJobsFetching}
                onChange={(value) => handleFilter('job_id', value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={jobOptions as any[]}
              />
            </div>
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Department"
                popupMatchSelectWidth={false}
                loading={isDeptFetching}
                onChange={(value) => handleFilter('department_id', value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={departmentOptions as any[]}
              />
            </div>
            <div>
              <Select
                allowClear
                size="large"
                placement="bottomRight"
                placeholder="Offer Status"
                popupMatchSelectWidth={false}
                onChange={(value) => handleFilter('offer_letter_status', value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'canceled', label: 'Canceled' },
                ]}
              />
            </div>
          </Flex>
          {!selectStatus ? (
            <Button
              size="large"
              onClick={() => handleFilter('select_status', true)}
              className="btn-primary-bordered"
            >
              Select for Hiring
            </Button>
          ) : (
            <Flex gap={16}>
              <Button
                size="large"
                disabled={isHiring}
                onClick={() => handleFilter('select_status', false)}
                className="btn-primary-bordered min-w-24"
              >
                Cancel
              </Button>
              <Button
                size="large"
                disabled={isHiring}
                onClick={() => {
                  if (!selected.length) {
                    toast('error', 'Please select applicants.');
                    return;
                  }
                  setConfirmOpen(true);
                }}
                className="btn-primary min-w-24"
              >
                Hire
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Cards */}
      <WithLoader isLoading={isLoading || isFetching}>
        {applicants.length === 0 ? (
          <EmptyState
            icon={<UserMultipleIcon size={40} />}
            title="No Applicants Found"
            description="No offer letters have been sent yet, or no applicants match your current filters."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {applicants.map((u) => {
              const isSelected = selected.includes(u.offer_letter_id);
              const canSelect =
                u.hiring_pipeline_status === null &&
                u.offer_letter_status === 'accepted';
              return (
                <div
                  key={u.offer_letter_id}
                  onClick={() => {
                    if (canSelect) handleSelect(u.offer_letter_id);
                  }}
                  className={cn(
                    'relative transition-all rounded-xl duration-200',
                    selectStatus && canSelect
                      ? 'cursor-pointer'
                      : 'cursor-auto',
                    isSelected && 'ring ring-theme-primary',
                    !canSelect && selectStatus && '!opacity-50',
                  )}
                >
                  <Flex
                    align="center"
                    justify="center"
                    className={cn(
                      'absolute -top-3 -right-3 w-7 h-7 bg-theme-primary rounded-full transition-all duration-200 z-10',
                      isSelected && canSelect
                        ? 'visible opacity-100'
                        : 'invisible opacity-0',
                    )}
                  >
                    <CheckIcon className="text-xl text-theme-slate-700" />
                  </Flex>

                  <EmpCard
                    item={{
                      name: u.applicant_name,
                      photo: u.photo,
                      designation: u.job_title,
                      department: u.department_name,
                      assessment_status:
                        u.hiring_pipeline_status || u.offer_letter_status,
                    }}
                    actionRender={
                      <div className="space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrawer({ data: u.offer_letter_id });
                          }}
                          className="text-theme-primary font-semibold cursor-pointer underline underline-offset-2"
                        >
                          View Offer Letter
                        </button>
                        {u.cv_link && (
                          <Flex
                            gap={4}
                            align="center"
                            justify="center"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(u.cv_link, '_blank');
                            }}
                            className="w-full p-1 bg-theme-gray-90 rounded text-theme-slate-800 text-sm cursor-pointer"
                          >
                            <PaperclipIcon
                              size={14}
                              className="text-[#007FC4]"
                            />
                            {firstChar(u.cv_link.split('/').pop() ?? 'CV')}
                            ...pdf
                          </Flex>
                        )}
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </WithLoader>

      {/* Confirm Hire */}
      <ConfirmationModal
        open={confirmOpen}
        loading={isHiring}
        title="Are you sure?"
        subTitle={`You want to hire ${selected.length} applicant${selected.length > 1 ? 's' : ''} from the hiring process`}
        buttonName="Yes, Hire"
        handleSubmit={handleHireApplicants}
        handleClose={() => setConfirmOpen(false)}
      />

      {/* Offer Letter Drawer */}
      <OfferLetterDrawer
        open={drawerConfig.open}
        onClose={closeDrawer}
        offerLetterId={drawerConfig.data}
      />
    </div>
  );
};

export default SentOfferLetter;
